import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getCountryOptions } from "../../../../services/countryService";
import { DOCUMENT_TYPES } from "../../../../constants/AppConstants";

/**
 * @typedef {Object} CountryOption
 * @property {string} id - ID del país
 * @property {string} name_es - Nombre del país en español
 * @property {string} name_en - Nombre del país en inglés
 * @property {string} dial_code - Código de marcación del país
 * @property {string} label - Etiqueta formateada para mostrar
 */

/**
 * @typedef {Object} DocumentTypeOption
 * @property {string} id - ID del tipo de documento
 * @property {string} value - Valor del tipo de documento
 * @property {string} label - Etiqueta del tipo de documento
 * @property {string} [name] - Nombre del tipo de documento
 */

/**
 * @typedef {Object} ButtonConfig
 * @property {boolean} outlined - Si el botón tiene estilo outlined
 * @property {string} size - Tamaño del botón ('xs', 'sm', 'md', 'lg')
 * @property {string} variant - Variante del botón ('PRIMARY', 'SECONDARY', etc.)
 * @property {string} icon - Nombre del ícono a mostrar
 * @property {string} className - Clases CSS del botón
 * @property {string} iconClassName - Clases CSS del ícono
 */

/**
 * @typedef {Object} UseCustomerDetailFormUIReturn
 * @property {Array<CountryOption>} countryOptions - Opciones de países para dropdown
 * @property {Array<DocumentTypeOption>} documentTypeOptions - Opciones de tipos de documento
 * @property {ButtonConfig} saveButtonConfig - Configuración del botón de guardado
 * @property {string} inputClassName - Clases CSS estándar para inputs
 * @property {function(string): Array<CountryOption>} countryFilterOption - Filtro de países
 * @property {function(CountryOption): JSX.Element} renderCountryOption - Renderizador de opción de país
 * @property {function(CountryOption): JSX.Element} renderSelectedCountry - Renderizador de país seleccionado
 * @property {boolean} isLoading - Estado de loading actual
 */

/**
 * Hook de UI Layer para configuraciones de presentación de CustomerDetailFormMC
 * 
 * @hook
 * @category UI Layer
 * @subcategory Customer Management
 * 
 * @description
 * Hook especializado para gestionar configuraciones de interfaz de usuario del formulario
 * CustomerDetailFormMC. Proporciona opciones memoizadas para dropdowns, configuraciones
 * de botones, filtros y renderizadores optimizados para componentes de presentación.
 * 
 * @features
 * - ✅ Opciones de países y tipos de documento memoizadas
 * - ✅ Configuración reactiva del botón de guardado según estado loading
 * - ✅ Filtros de búsqueda optimizados para dropdowns
 * - ✅ Renderizadores memoizados para opciones de componentes
 * - ✅ Clases CSS estandarizadas y reutilizables
 * - ✅ Separación completa de lógica de presentación
 * 
 * @responsibilities
 * ✅ Configuración de opciones para dropdowns (países, documentos)
 * ✅ Configuración de botones y elementos UI reactivos
 * ✅ Memoización de opciones estáticas para performance
 * ✅ Filtros y renderizadores para componentes de presentación
 * ✅ Gestión de clases CSS y estilos estandarizados
 * 
 * @restrictions
 * ❌ Lógica de negocio o llamadas API
 * ❌ Estado del formulario o validaciones
 * ❌ Transformación de datos
 * ❌ Operaciones CRUD o acciones de negocio
 * 
 * @performance
 * - useMemo para opciones estáticas (países, documentos)
 * - useCallback para filtros y renderizadores
 * - Memoización del valor de retorno completo
 * - Configuraciones reactivas sin re-creación innecesaria
 * 
 * @param {boolean} [loading=false] - Estado de loading de operaciones para configurar UI reactiva
 * 
 * @since 3.0.0
 * @version 3.1.0
 * 
 * @returns {UseCustomerDetailFormUIReturn} Objeto con configuraciones UI y handlers memoizados
 */
function useCustomerDetailFormUI(loading = false) {
  /**
   * Función de traducción para mensajes de interfaz
   * @type {function}
   */
  const { t } = useTranslation('InboxMC');

  /**
   * Opciones de países memoizadas para dropdown
   * 
   * @type {Array<CountryOption>}
   * @memoized
   * 
   * @description
   * Lista de países obtenida del servicio countryService, memoizada para
   * evitar re-cálculos en cada render. Incluye nombres en español e inglés,
   * códigos de marcación y etiquetas formateadas.
   */
  const countryOptions = useMemo(() => getCountryOptions(), []);

  /**
   * Opciones de tipos de documento memoizadas para dropdown
   * 
   * @type {Array<DocumentTypeOption>}
   * @memoized
   * 
   * @description
   * Lista de tipos de documento transformada desde DOCUMENT_TYPES,
   * mapeando la estructura para compatibilidad con componentes dropdown.
   * Se memoiza ya que es una lista estática.
   */
  const documentTypeOptions = useMemo(() => 
    DOCUMENT_TYPES.map(option => ({
      id: option.value,
      ...option
    })),
    []
  );

  /**
   * Configuración del botón de guardado memoizada y reactiva
   * 
   * @type {ButtonConfig}
   * @memoized
   * 
   * @description
   * Configuración completa del botón de guardado que reacciona al estado
   * de loading para mostrar ícono apropiado (checkmark o sync).
   * Incluye clases CSS de TailwindCSS para estilo consistente.
   * 
   * @reactive_properties
   * - icon: 'sync-outline' durante loading, 'checkmark-outline' en reposo
   * - Todas las demás propiedades permanecen constantes
   */
  const saveButtonConfig = useMemo(() => ({
    outlined: true,
    size: 'xs',
    variant: 'SECONDARY',
    icon: loading ? 'sync-outline' : 'checkmark-outline',
    className: 'tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 !tw-p-0 details-button-tooltip',
    iconClassName: 'tw-text-gray-600 !tw-w-4 !tw-h-4 tw-flex-shrink-0'
  }), [loading]);

  /**
   * Función de filtro para opciones de países memoizada
   * 
   * @function
   * @memoized
   * 
   * @param {string} searchTerm - Término de búsqueda para filtrar países
   * @returns {Array<CountryOption>} Array filtrado de opciones de países
   * 
   * @description
   * Filtra las opciones de países basado en el término de búsqueda.
   * Busca coincidencias en nombre español, nombre inglés y código de marcación.
   * Es case-insensitive para mejorar la experiencia de usuario.
   * 
   * @search_fields
   * - name_es: Nombre en español (case-insensitive)
   * - name_en: Nombre en inglés (case-insensitive)
   * - dial_code: Código de marcación (exact match)
   */
  const countryFilterOption = useCallback((searchTerm) =>
    countryOptions.filter(option =>
      option.name_es?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.dial_code?.includes(searchTerm)
    ),
    [countryOptions]
  );

  /**
   * Renderizador de opción de país memoizado
   * 
   * @function
   * @memoized
   * 
   * @param {CountryOption} option - Opción de país a renderizar
   * @returns {JSX.Element} Elemento JSX para mostrar la opción
   * 
   * @description
   * Componente de renderizado para opciones de país en dropdown.
   * Muestra la etiqueta formateada del país con estilos consistentes.
   * Se memoiza para evitar re-renders innecesarios de las opciones.
   */
  const renderCountryOption = useCallback((option) => (
    <div className="tw-flex tw-items-center">
      {option.label}
    </div>
  ), []);

  /**
   * Renderizador de país seleccionado memoizado
   * 
   * @function
   * @memoized
   * 
   * @param {CountryOption|null} option - Opción de país seleccionada
   * @returns {JSX.Element} Elemento JSX para mostrar la selección
   * 
   * @description
   * Componente de renderizado para el país seleccionado en dropdown.
   * Maneja casos donde no hay selección mostrando placeholder traducido.
   * Se memoiza junto con función de traducción para estabilidad.
   */
  const renderSelectedCountry = useCallback((option) => (
    <div className="tw-flex tw-items-center tw-overflow-hidden">
      {option?.label || t('customerDetails.selectPlaceholder')}
    </div>
  ), [t]);

  /**
   * Clases CSS estándar para inputs memoizadas
   * 
   * @type {string}
   * @memoized
   * 
   * @description
   * Clases CSS estandarizadas para todos los inputs del formulario.
   * Incluye reset de estilos de focus y spacing consistente.
   * Se memoiza ya que es una string estática.
   * 
   * @styles
   * - focus:tw-ring-0: Remove focus ring
   * - focus:tw-shadow-none: Remove focus shadow
   * - focus:tw-outline-none: Remove focus outline
   * - focus:tw-ring-transparent: Transparent ring
   * - tw-mb-3: Bottom margin for spacing
   */
  const inputClassName = useMemo(() => 
    "focus:tw-ring-0 focus:tw-shadow-none focus:tw-outline-none focus:tw-ring-transparent tw-mb-3",
    []
  );

  // OPTIMIZACIÓN: Memoizar valor de retorno
  const returnValue = useMemo(() => ({
    // Opciones para componentes
    countryOptions,
    documentTypeOptions,
    
    // Configuraciones de UI
    saveButtonConfig,
    inputClassName,
    
    // Handlers y filtros
    countryFilterOption,
    renderCountryOption,
    renderSelectedCountry,
    
    // Estado de UI
    isLoading: loading
  }), [
    countryOptions,
    documentTypeOptions,
    saveButtonConfig,
    inputClassName,
    countryFilterOption,
    renderCountryOption,
    renderSelectedCountry,
    loading
  ]);

  return returnValue;
}

export default useCustomerDetailFormUI;